import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { RootState } from "@/shared/redux/store/store";
import { mergeStructuredOcrDocuments } from "@/shared/utils/documentOcr";
import { HEALTH_QUOTE_STEP_KEYS, HealthQuoteStepKey } from "../../common/static";
import {
  HealthAdditionalInfoValues,
  HealthDetailsFormValues,
  HealthDocumentUploadFieldKey,
  HealthDocumentUploadValues,
  HealthHabitsFormValues,
  HealthPaymentValues,
  HealthPersonalFormValues,
  HealthQuickQuestionsFormValues,
  HealthQuoteFormState,
  HealthQuoteSelectionValues,
  HealthUploadedDocumentMap,
  SaveHealthQuoteStepPayload,
} from "../type";

export const defaultHealthPersonalFormValues: HealthPersonalFormValues = {
  firstName: "",
  lastName: "",
  mobileNumber: {
    countryCode: "971",
    countryCodeIso: "AE",
    number: "",
  },
  email: "",
  partnerCode: "",
  friendsAndFamilyContact: "",
  marketingConsent: "no",
};

export const defaultHealthDetailsFormValues: HealthDetailsFormValues = {
  coverageAmount: "500000",
  policyType: "newPolicy",
  coverageType: "individual",
  currentInsurer: "",
  policyExpiryDate: "",
  sumInsured: "",
  claimsInLast3Years: "",
  members: [
    {
      name: "",
      relationship: "self",
      dob: "",
      preExistingCondition: false,
    },
  ],
  startDate: "",
  policy_tenure: "1",
};

export const defaultHealthHabitsFormValues: HealthHabitsFormValues = {
  selectedHabits: [],
  preExistingMedicalConditions: "",
  hospitalizedLast2Years: "",
  currentlyOnMedication: "",
  surgeryLast5Years: "",
  allergies: "",
  tobaccoOrNicotine: "",
  familyHistoryMajorIllness: "",
  otherMedicalCondition: "",
};

export const defaultHealthQuickQuestionsFormValues: HealthQuickQuestionsFormValues = {
  heightCm: "",
  weightKg: "",
  bmi: "",
  medicalConditions: [],
  occupationType: "",
  workProfile: "",
  annualIncomeRange: "",
  additionalInformation: "",
  otherMedicalConditionDetails: "",
};

export const defaultHealthQuoteSelectionValues: HealthQuoteSelectionValues = {
  selectedQuoteId: "",
};

export const defaultHealthDocumentUploadValues: HealthDocumentUploadValues = {
  previousPolicy: false,
  healthInsuranceCard: false,
  medicalReport: false,
  confirmDocuments: false,
};

export const defaultHealthAdditionalInfoValues: HealthAdditionalInfoValues = {
  gender: "",
  nationality: "",
  cityOfResidence: "",
  address: "",
  emiratesId: "",
  passportNumber: "",
  sourceOfFund: "",
  beneficiaryName: "",
  beneficiaryRelation: "",
  uaeResidentConfirm: false,
};

export const defaultHealthPaymentValues: HealthPaymentValues = {
  paymentMethod: "Card",
  billingEmail: "",
  cardHolder: "",
  cardNumber: "",
  expiry: "",
  cvv: "",
  confirmPayment: false,
};

export const defaultHealthQuoteFormState: HealthQuoteFormState = {
  request: null,
  documentUploadDocuments: {},
  [HEALTH_QUOTE_STEP_KEYS.PERSONAL_DETAILS]: defaultHealthPersonalFormValues,
  [HEALTH_QUOTE_STEP_KEYS.HEALTH_DETAILS]: defaultHealthDetailsFormValues,
  [HEALTH_QUOTE_STEP_KEYS.HEALTH_HABITS]: defaultHealthHabitsFormValues,
  [HEALTH_QUOTE_STEP_KEYS.QUICK_QUESTIONS]: defaultHealthQuickQuestionsFormValues,
  [HEALTH_QUOTE_STEP_KEYS.QUOTE_SELECTION]: defaultHealthQuoteSelectionValues,
  [HEALTH_QUOTE_STEP_KEYS.DOCUMENT_UPLOAD]: defaultHealthDocumentUploadValues,
  [HEALTH_QUOTE_STEP_KEYS.ADDITIONAL_INFO]: defaultHealthAdditionalInfoValues,
  [HEALTH_QUOTE_STEP_KEYS.PAYMENT]: defaultHealthPaymentValues,
};

function createSaveHealthQuoteStepPayload<TStepKey extends HealthQuoteStepKey>(
  stepName: TStepKey,
  data: HealthQuoteFormState[TStepKey]
): Extract<SaveHealthQuoteStepPayload, { stepName: TStepKey }> {
  return { stepName, data } as Extract<SaveHealthQuoteStepPayload, { stepName: TStepKey }>;
}

export function saveHealthQuoteStepFor<TStepKey extends HealthQuoteStepKey>(
  stepName: TStepKey,
  data: HealthQuoteFormState[TStepKey]
) {
  return saveHealthQuoteStep(createSaveHealthQuoteStepPayload(stepName, data));
}

const healthQuoteSlice = createSlice({
  name: "healthQuote",
  initialState: defaultHealthQuoteFormState,
  reducers: {
    saveHealthQuoteStep(state, action: PayloadAction<SaveHealthQuoteStepPayload>) {
      switch (action.payload.stepName) {
        case HEALTH_QUOTE_STEP_KEYS.PERSONAL_DETAILS:
          state[HEALTH_QUOTE_STEP_KEYS.PERSONAL_DETAILS] = action.payload.data;
          break;
        case HEALTH_QUOTE_STEP_KEYS.HEALTH_DETAILS:
          state[HEALTH_QUOTE_STEP_KEYS.HEALTH_DETAILS] = action.payload.data;
          break;
        case HEALTH_QUOTE_STEP_KEYS.HEALTH_HABITS:
          state[HEALTH_QUOTE_STEP_KEYS.HEALTH_HABITS] = action.payload.data;
          break;
        case HEALTH_QUOTE_STEP_KEYS.QUICK_QUESTIONS:
          state[HEALTH_QUOTE_STEP_KEYS.QUICK_QUESTIONS] = action.payload.data;
          break;
        case HEALTH_QUOTE_STEP_KEYS.QUOTE_SELECTION:
          state[HEALTH_QUOTE_STEP_KEYS.QUOTE_SELECTION] = action.payload.data;
          break;
        case HEALTH_QUOTE_STEP_KEYS.DOCUMENT_UPLOAD:
          state[HEALTH_QUOTE_STEP_KEYS.DOCUMENT_UPLOAD] = action.payload.data;
          break;
        case HEALTH_QUOTE_STEP_KEYS.ADDITIONAL_INFO:
          state[HEALTH_QUOTE_STEP_KEYS.ADDITIONAL_INFO] = action.payload.data;
          break;
        case HEALTH_QUOTE_STEP_KEYS.PAYMENT:
          state[HEALTH_QUOTE_STEP_KEYS.PAYMENT] = action.payload.data;
          break;
      }
    },

    resetHealthQuoteState() {
      return defaultHealthQuoteFormState;
    },

    saveHealthUploadedDocument(
      state,
      action: PayloadAction<{
        fieldKey: HealthDocumentUploadFieldKey;
        document: HealthUploadedDocumentMap[HealthDocumentUploadFieldKey];
      }>
    ) {
      const { fieldKey, document } = action.payload;

      if (!document) {
        delete state.documentUploadDocuments[fieldKey];
        return;
      }

      state.documentUploadDocuments[fieldKey] = document;
    },
  },
});

export const { resetHealthQuoteState, saveHealthQuoteStep, saveHealthUploadedDocument } =
  healthQuoteSlice.actions;

export const selectHealthQuoteState = (state: RootState) => state.healthQuote;

export const selectHealthUploadedDocuments = (state: RootState) =>
  state.healthQuote.documentUploadDocuments;

export const selectHealthStructuredOcrData = (state: RootState) =>
  mergeStructuredOcrDocuments(state.healthQuote.documentUploadDocuments);

export default healthQuoteSlice.reducer;
